import React, {useMemo, useState} from 'react';
import {
    Btn,
    ConfirmationContainer,
    FlexColumnSection,
    FlexRowSection,
    SeparatorHorizontal,
    SmallHeader,
    SmallText
} from "../../styles/globalStyles";
import {useDispatch, useSelector} from "react-redux";
import {projectTypeMainSlice} from "@arboxappv4/shared/src/helpers/constants";
import UserAvatar from "../../components/UserAvatar";
import dayjs from "dayjs";
import {DB_DATE_FORMAT, DB_TIME_FORMAT} from "@arboxappv4/shared/src/constants/constants";
import {t} from "i18next";
import useScreenSize from "../../helpers/hooks/useScreenSize";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {regular} from "@fortawesome/fontawesome-svg-core/import.macro";
import {DisabledCta} from "../../styles/globalStyles";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import {OPTIONS_CONFIRMED} from "../../../../src/constants";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {CONFIRMATION_MODAL, openModal} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {setKioskError} from "@arboxappv4/shared/src/redux/kioskStandAlone/kioskSlice";

const ArrivalConfirmation = () => {
    const dispatch = useDispatch();
    const {isMobile} = useScreenSize()
    const project_type = useSelector(state => state.project?.project_type);
    const language = useSelector(state => state[projectTypeMainSlice[project_type]]?.language);
    const selectedLocation = useSelector(state => state[projectTypeMainSlice[project_type]]?.selectedLocation);
    const scheduleUser = useSelector(state => state.kiosk?.scheduleUser)
    const arrival_status = scheduleUser?.schedule_user_arrivals?.arrival_statuses
    const isConfirmed = arrival_status?.status === OPTIONS_CONFIRMED
    const confirmationSettings = useSelector(state => state.kiosk?.confirmationSettings)
    const [confirmed, setConfirmed] = useState(isConfirmed)
    const [cancelled, setCancelled] = useState(false)
    const [loaderConfirm, setLoaderConfirm] = useState(false)
    const [loaderCancel, setLoaderCancel] = useState(false)
    const dateLabel = useMemo(() => dayjs(scheduleUser.schedule.date, DB_DATE_FORMAT).locale(language?.code ?? 'en').format(`dddd, ${language?.code === 'he' ? 'D MMMM' : 'MMMM D'}, YYYY`), [scheduleUser, language]);
    const timeLabel = useMemo(() => dayjs(scheduleUser.schedule.time, 'HH:mm').format(selectedLocation.time_format ?? 'HH:mm'), [scheduleUser, selectedLocation]);
    const isDatePassed = useMemo(() => dayjs(scheduleUser?.schedule?.date_time?.date, DB_DATE_FORMAT + ' ' + DB_TIME_FORMAT).isBefore(dayjs()), [scheduleUser]);
    const disableConfirmBtn = useMemo(() => scheduleUser.checked_in || isDatePassed || !scheduleUser.registered || scheduleUser.deleted_at, [scheduleUser, isDatePassed]);
    const displayCancelBtn = useMemo(() => confirmationSettings?.allowCancel && !disableConfirmBtn, [confirmationSettings, disableConfirmBtn]);

    const onConfirm = async () => {
        try {
            setLoaderConfirm(true)
            await apiAction('setConfirmArrivalStatus', 'post', {schedule_user_id: scheduleUser.id});
            setLoaderConfirm(false)
            setConfirmed(true)
        } catch (e) {
            console.log('e', e)
        }
    }

    const onCancelBooking = async (lateCancellation) => {
        try {
            setLoaderCancel(true)
            await apiAction('kiosk/scheduleUser/delete', 'post', {schedule_user_id: scheduleUser.id, schedule_id: scheduleUser.schedule_fk, late_cancel: lateCancellation});
            setLoaderCancel(false)
            setCancelled(true)
        } catch (error) {
            console.log('error', error)
            dispatch(setKioskError(error.error?.messageToUser ?? t('cancel-booking-error')))
        }
    }

    const openCancelBookingConfirm = async () => {
        let lateCancellation = false
        try {
            await apiAction('kiosk/scheduleUser/checkLateCancel', 'post', {schedule_id: scheduleUser.schedule_fk});
        } catch (error) {
            lateCancellation = error.error.code === 513
        }
        dispatch(openModal({modalName: CONFIRMATION_MODAL, props: {
                header: t(`cancelScheduleUser-confirm-header`),
                explanation: t(`cancelScheduleUser${lateCancellation ? '-late' : ''}-confirm-explain`),
                submitText: t('booking-cancel-btn'),
                onConfirm: () => onCancelBooking(lateCancellation)
            }}))
    }

    const getConfirmBtnText = () => {
        if(!scheduleUser.registered || scheduleUser.deleted_at) {
            return t('cancelled-session-label')
        } else if(isDatePassed) {
            return t('appointment-date-past-disable')
        } else if(scheduleUser.checked_in) {
            return t('checked-in-disable')
        }
        return t(`confirm-arrival${isMobile ? '-mobile': ''}`)
    }

    return (
        <FlexColumnSection flex={'unset'} overflow={'auto'}>
            <div style={{width: isMobile ? '100%' : '590px'}}>
                <ConfirmationContainer style={{flex: 'unset'}}>
                    <FlexColumnSection gap={'10px'} width={'100%'} padding={'16px'}>
                        <SmallHeader fontSize={'18px'}>{scheduleUser?.schedule.box_categories?.name}</SmallHeader>
                        <FlexColumnSection gap={'6px'}>
                            <FlexRowSection gap={'8px'} justify={'start'}>
                                <SmallText weight={'600'}>{t('date-confirmation')}:</SmallText>
                                <SmallText>{dateLabel}</SmallText>
                            </FlexRowSection>
                            <FlexRowSection gap={'8px'} justify={'start'}>
                                <SmallText weight={'600'}>{t('time-confirmation')}:</SmallText>
                                <SmallText>{timeLabel}</SmallText>
                            </FlexRowSection>
                            {scheduleUser.schedule.coach &&
                                <FlexRowSection gap={'8px'} justify={'start'}>
                                    <UserAvatar
                                        user={{
                                            ...scheduleUser.schedule.coach,
                                            cloudinary_image: scheduleUser.schedule.coach?.image || (scheduleUser.schedule.coach?.image_public_id_cloudinary ? `https://res.cloudinary.com/arbox/image/upload/c_fit,h_500,w_500,f_auto,q_auto/${scheduleUser.schedule.coach.image_public_id_cloudinary}.jpg` : undefined),
                                            user_image: scheduleUser.schedule.coach?.image
                                        }}
                                        nameType={'full'}
                                    />
                                </FlexRowSection>
                            }
                        </FlexColumnSection>
                        {displayCancelBtn && selectedLocation.cancellation_policy && <SeparatorHorizontal margin={'12px 0'}/>}
                        <FlexColumnSection gap={'6px'}>
                            <SmallText weight={'500'}>{t('appointment-cancellation-header')}:</SmallText>
                            {selectedLocation.cancellation_policy}
                        </FlexColumnSection>
                        {confirmationSettings?.confirmationText && <SeparatorHorizontal margin={'12px 0'}/>}
                        <FlexColumnSection gap={'6px'}>
                            {displayCancelBtn && selectedLocation.cancellation_policy && <SmallText weight={'500'}>{t('text-confirmation-header')}:</SmallText>}
                            {confirmationSettings?.confirmationText}
                        </FlexColumnSection>
                        {!confirmed && !cancelled &&
                            <FlexRowSection gap={'10px'}>
                                <Btn type={'primary'} flex={1} loading={loaderConfirm} onClick={onConfirm} className={'site-primary-btn'} disabled={disableConfirmBtn} data-date-passed={isDatePassed} style={{display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', fontWeight: 600}}>
                                    {!loaderConfirm && <FontAwesomeIcon icon={regular('calendar-check')}/>}
                                    {getConfirmBtnText()}
                                </Btn>
                                {displayCancelBtn &&
                                <Btn danger loading={loaderCancel} flex={1} onClick={openCancelBookingConfirm} className={'site-primary-btn'} style={{display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', fontWeight: 600}}>
                                    {!loaderCancel && <FontAwesomeIcon icon={regular('xmark')}/>}
                                    {t(`cancel-booking-btn${isMobile ? '-mobile': ''}`)}
                                </Btn>
                                }
                            </FlexRowSection>
                        }
                    </FlexColumnSection>
                    {confirmed && !loaderConfirm && <DisabledCta width={'100%'} bg={Colors.confirmedBadge} br={'0 0 10px 10px'} borderTop={`1px solid ${Colors.siteScheduleBorder}`}>{t('arrival-confirmed')}</DisabledCta>}
                    {cancelled && !loaderCancel && <DisabledCta width={'100%'} bg={Colors.badgeRed} br={'0 0 10px 10px'} borderTop={`1px solid ${Colors.siteScheduleBorder}`}>{t('arrival-cancelled')}</DisabledCta>}
                </ConfirmationContainer>
            </div>
        </FlexColumnSection>
    )
}

export default ArrivalConfirmation